import { create } from "zustand/react";
import type { UserDetails } from "@/types/user.ts";
import { type TutorDetails, TutorStatus } from "@/types/tutor.ts";
import { None, type Option, Some } from "ts-results-es";
import { PRODUCTION } from "@/constants/settings.ts";

export type UserStore = {
  // Vars
  userId: number;
  tutorId: number;
  auth: Auth;
  userDetails: UserDetails | null;
  tutorDetails: TutorDetails | null;
  settings: UserSettings;

  valid: boolean | null;

  statusUpdateEvent: Option<TutorStatus>;

  // Funcs
  Startup: () => boolean;
  SetSession: (token: string) => void;
  SetUserDetails: (details: UserDetails) => void;
  SetTutorDetails: (details: TutorDetails) => void;
  SetCapacity: (capacity: number) => void;
  SetStatus: (status: TutorStatus) => void;
  SetGroupJoined: (groupJoined: boolean) => void;
  Logout: (forceRefresh?: boolean) => void;
  SetStatusEvent: (statusEvent: Option<TutorStatus>) => void;
};

interface UserSettings {
  capacity: number;
  status: TutorStatus;
  groupJoined: boolean;
}

export type Auth = {
  token: string | null;
};

const TOKEN_KEY = PRODUCTION ? "eedi-prod-session-key" : "session_storage_key";

export const useUserStore = create<UserStore>()((set, get) => ({
  userId: -1,
  tutorId: -1,
  valid: null,
  auth: {
    token: null,
  },
  userDetails: null,
  tutorDetails: null,
  settings: {
    capacity: 1,
    status: TutorStatus.Offline,
    groupJoined: false,
  },
  statusUpdateEvent: None,
  Startup: () => {
    const token = window.localStorage.getItem(TOKEN_KEY);
    if (token) {
      set(() => ({
        auth: {
          token,
        },
        valid: true,
      }));
    } else {
      set(() => ({
        valid: false,
      }));
    }

    return !!token;
  },
  SetUserDetails: (details: UserDetails) => {
    set(() => ({
      userId: details.userId,
      userDetails: details,
    }));
  },
  SetTutorDetails: (details: TutorDetails) => {
    const status = details.status === "Available" ? TutorStatus.Available : TutorStatus.Offline;
    set((store) => ({
      tutorId: details.tutorId,
      tutorDetails: details,
      settings: {
        ...store.settings,
        capacity: details.capacity ?? 1,
        status: status,
      },
    }));

    const { SetStatusEvent } = get();
    SetStatusEvent(Some(status));
  },
  SetSession: (token: string) => {
    window.localStorage.setItem(TOKEN_KEY, token);

    set(() => ({
      auth: {
        token,
      },
      valid: true,
    }));
  },
  SetCapacity: (capacity: number) => {
    set((store) => ({
      settings: {
        ...store.settings,
        capacity,
      },
    }));
  },
  SetStatus: (status: TutorStatus) => {
    set((store) => ({
      settings: {
        ...store.settings,
        status,
      },
    }));
  },
  SetGroupJoined: (groupJoined: boolean) => {
    set((store) => ({
      settings: {
        ...store.settings,
        groupJoined,
      },
    }));
  },
  Logout: (forceRefresh) => {
    window.localStorage.removeItem(TOKEN_KEY);
    set(() => ({
      userId: -1,
      tutorId: -1,
      userDetails: null,
      tutorDetails: null,
      valid: null,
      auth: {
        token: null,
      },
      settings: {
        status: TutorStatus.Offline,
        capacity: 1,
        groupJoined: false,
      },
    }));
    if (forceRefresh) window.location.assign("/auth/login");
  },
  SetStatusEvent: (statusEvent: Option<TutorStatus>) => {
    set(() => ({
      statusUpdateEvent: statusEvent,
    }));
  },
}));
